// Chord Craft hub — the landing page at /chord-craft. Hero, the origin-story
// decode card (the YouTube-short chord mystery that started this whole
// feature), and four room cards linking into the rest of the sub-site.
// Presentational only; each room owns its own state.

import React from "react";
import { Link } from "react-router-dom";
import CraftShell from "../components/chordCraft/CraftShell";

// Literal per-room class strings (not template-composed) so Tailwind's JIT
// scanner picks them up straight from this file.
const ROOM_CARDS = [
  {
    id: "palette",
    path: "/chord-craft/palette",
    name: "Palette",
    tagline: "Color chords with extensions and hear what mood they set.",
    accentText: "text-fuchsia-400",
    accentBorder: "hover:border-fuchsia-400/60",
    accentGlow: "group-hover:shadow-[0_0_24px_rgba(232,121,249,0.25)]",
  },
  {
    id: "gym",
    path: "/chord-craft/gym",
    name: "Voicing Gym",
    tagline: "Split a chord across two hands, from anchor to rootless.",
    accentText: "text-amber-400",
    accentBorder: "hover:border-amber-400/60",
    accentGlow: "group-hover:shadow-[0_0_24px_rgba(251,191,36,0.2)]",
  },
  {
    id: "slither",
    path: "/chord-craft/slither",
    name: "Slither",
    tagline: "Connect chords with the smallest possible moves.",
    accentText: "text-emerald-400",
    accentBorder: "hover:border-emerald-400/60",
    accentGlow: "group-hover:shadow-[0_0_24px_rgba(52,211,153,0.2)]",
  },
  {
    id: "loop",
    path: "/chord-craft/loop",
    name: "Loop Room",
    tagline: "Take progressions from everywhere, loop them, and jam.",
    accentText: "text-sky-400",
    accentBorder: "hover:border-sky-400/60",
    accentGlow: "group-hover:shadow-[0_0_24px_rgba(56,189,248,0.2)]",
  },
];

export default function ChordCraftHome() {
  return (
    <CraftShell
      room="home"
      title="Chord Craft"
      subtitle="Get really, really good at chords — color them, voice them, connect them, use them."
    >
      <div className="mt-2 rounded-2xl border border-stone-800 bg-stone-900/80 p-6 sm:p-8">
        <p className="text-xs uppercase tracking-widest text-stone-500 mb-3">
          how this started
        </p>
        <p className="font-serif text-2xl sm:text-3xl text-amber-50 mb-4">
          Gm9 → C13♭9 → F6/9 ... &ldquo;a 2-5-1 in G&rdquo;?!
        </p>
        <div className="space-y-2 text-stone-300 leading-relaxed">
          <p>
            A chord-theory short flashed that progression and called it a
            "2-5-1 in G" — which sounds wrong until you notice it's really a
            ii–V–I that just <em>starts</em> on G, landing home in the key of
            F.
          </p>
          <p>
            Strip the fancy symbols and it's three plain chords —
            Gm, C, F — wearing color: the 9, the ♭9, the 6/9 are toppings,
            not a different chord underneath.
          </p>
          <p>
            Play it on a real keyboard and the trick shows itself: one hand
            holds the skeleton, the other hand holds the color.
          </p>
          <p className="text-stone-400 italic pt-1">
            Every room here teaches one piece of that trick.
          </p>
        </div>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {ROOM_CARDS.map((room) => (
          <Link
            key={room.id}
            to={room.path}
            className={`group block rounded-2xl border border-stone-800 bg-stone-900/80 p-5 transition-all ${room.accentBorder} ${room.accentGlow}`}
          >
            <p className={`font-serif text-xl mb-1 ${room.accentText}`}>{room.name}</p>
            <p className="text-stone-300 text-sm leading-relaxed">{room.tagline}</p>
          </Link>
        ))}
      </div>

      <p className="mt-6 text-center text-xs text-stone-500">
        MIDI keyboard optional — everything here is playable on-screen.
      </p>
    </CraftShell>
  );
}
